<template>
    <div>
        <b-row>
            <b-col cols="12">
                <template v-if="isLoading">
                    <span class="icon mr-1">
                        <FontAwesomeIcon icon="spinner" spin/>
                    </span>
                    <span class="label">
                        {{ $t('omt.gui.general.label.loading') }}&hellip;
                    </span>
                </template>

                <template v-else>
                    <b-row class="responsible-location-form mb-4" v-if="hasPermissionForAllSalesChannels">
                        <b-col cols="10">
                            Toon alleen locaties van hetzelfde sales channel ({{ salesOrder.sales_channel_identifier }}) als de order
                        </b-col>
                        <b-col cols="2">
                            <b-check switch v-model="showOnlyLocationsFromSameSalesChannel" :disabled="!hasPermissionForAllSalesChannels"/>
                        </b-col>
                    </b-row>
                    <b-row class="responsible-location-form mb-3">
                        <b-col cols="5">
                            {{ $t('omt.gui.orders.choose-responsible-location.choose-location-question') }}
                        </b-col>
                        <b-col cols="7">
                            <b-form-select
                                v-model="responsibleLocation"
                                :options="responsibleLocationsFormOptions"
                                label="Responsible Location"
                                outlined
                                dense
                                required
                            >
                                <template #first>
                                    <b-form-select-option
                                        :value="null"
                                        disabled
                                    >
                                        {{ $t('omt.gui.general.action.select-one') }}
                                    </b-form-select-option>
                                </template>
                            </b-form-select>
                        </b-col>
                    </b-row>
                    <b-row class="responsible-location-form mb-3">
                        <b-col cols="5">
                            {{ $t('omt.gui.orders.choose-responsible-location.choose-dispatch-type-question') }}
                        </b-col>
                        <b-col cols="7">
                            <b-overlay :show="isLoadingDispatchTypes" spinner-small>
                                <b-form-select
                                    v-model="dispatchType" :options="dispatchTypesForLocation"
                                    label="Dispatch type" value-field="identifier" text-field="display_name"
                                    :disabled="!responsibleLocation"
                                    outlined dense required
                                >
                                    <template #first>
                                        <b-form-select-option
                                            :value="null"
                                            disabled
                                        >
                                            {{ $t('omt.gui.general.action.select-one') }}
                                        </b-form-select-option>
                                    </template>
                                </b-form-select>
                            </b-overlay>
                        </b-col>
                    </b-row>
                </template>
            </b-col>
        </b-row>
    </div>
</template>

<script>
export default {
    name: "ChooseResponsibleLocation",

    props: {
        salesOrder: {required: true},
    },

    data: () => ({
        isLoading: true,
        isLoadingDispatchTypes: false,
        responsibleLocation: null,
        dispatchType: '',
        dispatchTypesForLocation: [],
        showOnlyLocationsFromSameSalesChannel: false,
    }),

    mounted() {
        this.showOnlyLocationsFromSameSalesChannel = !this.hasPermissionForAllSalesChannels

        this.$store.getters.getAllLocations
            .then(() => {
                this.isLoading = false
            })
    },

    computed: {
        responsibleLocations() {
            return this.$store.state.allLocations
                .filter((location) => location.location_type.identifier === "store" && location.active === '1')
                .filter((location) => {
                    if (!this.showOnlyLocationsFromSameSalesChannel) {
                        return true
                    }

                    return location.location_properties['supportsSalesChannels']?.includes(this.salesOrder.sales_channel_identifier)
                })
                .sort((a, b) => a.display_name.localeCompare(b.display_name));
        },
        responsibleLocationsFormOptions() {
            // responsibleLocations, but in the format required by b-form-select (see https://bootstrap-vue.org/docs/components/form-select/)
            return this.responsibleLocations.map((location) => ({
                value: location.id,
                text: `${location.display_name} (${location.identifier})`,
            }));
        },
        selectedLocation() {
            return this.responsibleLocations.find(l => l.id === this.responsibleLocation)
        },
        selectedLocationDispatchType() {
            return this.dispatchTypesForLocation
                .find(d => d.identifier === this.dispatchType)
        },
        selectedLocationDispatchTypeActiveCarrier() {
            return this.selectedLocationDispatchType.carriers
                ?.find(c => c.carrier_methods.some(m => m.is_active && m.is_active_for_location))
                ?? []
        },
        selectedLocationDispatchTypeActiveCarrierMethod() {
            return this.selectedLocationDispatchTypeActiveCarrier.carrier_methods
                ?.find(m => m.is_active && m.is_active_for_location)
                ?? []
        },
        hasPermissionForAllSalesChannels() {
            return this.hasPermission('orders.dispatchrequests.responsiblelocation.edit.allsaleschannels')
        },
    },
    methods: {
        handleChange() {
            this.value = this.responsibleLocation;
            this.$emit("dataChange", this.responsibleLocation)
        },
        async getDispatchTypesForLocation() {
            this.isLoadingDispatchTypes = true
            return this.$store.dispatch('loadDispatchInfoForLocation', {type: this.selectedLocation?.location_type?.identifier, identifier: this.selectedLocation?.identifier})
                .then(response => {
                    const excludedDispatchTypes = this.$store.state.config.settings['frontend-parameters.dispatch-requests']['choose-responsible-location-exclude-dispatch-types']

                    this.dispatchTypesForLocation = this.$store.getters.getDispatchInfoForLocation(this.selectedLocation?.location_type?.identifier, this.selectedLocation?.identifier)
                        .filter(d => d.is_active && d.is_active_for_location && !excludedDispatchTypes?.split(',').includes(d.identifier))

                    if (this.dispatchTypesForLocation.length === 0) {
                        this.$alert('Deze locatie heeft geen actieve dispatchtypes', 'danger')
                        return
                    }

                    if (this.dispatchTypesForLocation.length === 1) {
                        this.dispatchType = this.dispatchTypesForLocation[0].identifier
                    }
                })
                .catch(e => {
                    console.error(e)
                    this.$alert('Fout bij het ophalen van de dispatchtypes', 'danger')
                })
                .finally(() => this.isLoadingDispatchTypes = false)
        },
    },

    watch: {
        responsibleLocation(newVal) {
            this.dispatchType = ''
            if (!newVal) {
                return
            }
            this.getDispatchTypesForLocation()
        },
        dispatchType(newVal) {
            if (!newVal) {
                this.$emit('dataChange')
                return
            }

            this.$emit(
                'dataChange',
                this.selectedLocation.id,
                this.selectedLocation.location_type.identifier,
                this.selectedLocation.identifier,
                this.selectedLocationDispatchType?.identifier ?? null,
                this.selectedLocationDispatchTypeActiveCarrier?.identifier ?? null,
                this.selectedLocationDispatchTypeActiveCarrierMethod?.identifier ?? null,
            )
        },
        showOnlyLocationsFromSameSalesChannel() {
            // When the location filter is toggled check if the selected location is in the now filtered list of locations. If not: clear the selection.
            if (!this.responsibleLocations.some(l => l.id === this.responsibleLocation)) {
                this.responsibleLocation = null
            }
        },
    },
};
</script>
